import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  ChartBarIcon,
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { apiClient } from '../utils/api';
import { formatNumber, formatPercentage, cn } from '../utils/helpers';
import io, { Socket } from 'socket.io-client';
import { getSocketUrl } from '../utils/api';

interface TradingPeriodStats {
  profit: number;
  trades: number;
  winRate: number;
  profitPercentage: number;
  avgProfit: number;
  bestTrade: number;
  worstTrade: number;
}

interface TradingStatsData {
  daily: TradingPeriodStats;
  weekly: TradingPeriodStats;
  monthly: TradingPeriodStats;
  allTime: {
    totalProfit: number;
    totalTrades: number;
    avgWinRate: number;
  };
}

type Period = 'daily' | 'weekly' | 'monthly';

const PERIOD_LABELS: Record<Period, string> = {
  daily: '일일',
  weekly: '주간',
  monthly: '월간'
};

const TradingStats: React.FC = () => {
  const { theme } = useTheme();
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('daily');
  const [statsData, setStatsData] = useState<TradingStatsData>({
    daily: { profit: 0, trades: 0, winRate: 0, profitPercentage: 0, avgProfit: 0, bestTrade: 0, worstTrade: 0 },
    weekly: { profit: 0, trades: 0, winRate: 0, profitPercentage: 0, avgProfit: 0, bestTrade: 0, worstTrade: 0 },
    monthly: { profit: 0, trades: 0, winRate: 0, profitPercentage: 0, avgProfit: 0, bestTrade: 0, worstTrade: 0 },
    allTime: { totalProfit: 0, totalTrades: 0, avgWinRate: 0 }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [socket, setSocket] = useState<Socket | null>(null);

  // Fetch trading stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await apiClient.get<TradingStatsData>('/api/trading/stats');
        if (response.success && response.data) {
          setStatsData(response.data);
        }
      } catch (error) {
        console.error('Error fetching trading stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  // WebSocket connection
  useEffect(() => {
    const socketInstance = io(getSocketUrl(), {
      path: '/socket.io/',
      transports: ['websocket', 'polling'],
    });

    socketInstance.on('tradingStats', (data: TradingStatsData) => {
      setStatsData(data);
    });

    setSocket(socketInstance);

    return () => {
      socketInstance.disconnect();
    };
  }, []);

  // Get current period stats
  const currentStats = useMemo(() => statsData[selectedPeriod], [statsData, selectedPeriod]);

  // Calculate derived values
  const derivedStats = useMemo(() => {
    const { daily, weekly, monthly, allTime } = statsData;
    
    return {
      totalProfit: allTime.totalProfit || (daily.profit + weekly.profit + monthly.profit),
      avgWinRate: allTime.avgWinRate || ((daily.winRate + weekly.winRate + monthly.winRate) / 3),
      trend: currentStats.profit > 0 ? 'up' : currentStats.profit < 0 ? 'down' : 'neutral'
    };
  }, [statsData, currentStats]);

  // Handle period change
  const handlePeriodChange = useCallback((period: Period) => {
    setSelectedPeriod(period);
  }, []);

  // Period selector buttons
  const periodButtons = useMemo(() => (
    <div className="flex space-x-2 bg-gray-100 dark:bg-gray-700 rounded-2xl p-1.5">
      {(Object.keys(PERIOD_LABELS) as Period[]).map((period) => (
        <button
          key={period}
          onClick={() => handlePeriodChange(period)}
          className={cn(
            'flex-1 py-3 px-4 rounded-xl font-semibold transition-all duration-300',
            selectedPeriod === period
              ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-md'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          )}
        >
          {PERIOD_LABELS[period]} 수익
        </button>
      ))}
    </div>
  ), [selectedPeriod, handlePeriodChange]);

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full flex flex-col p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-300 dark:bg-gray-700 rounded w-1/3" />
          <div className="h-12 bg-gray-300 dark:bg-gray-700 rounded-2xl" />
          <div className="h-32 bg-gray-300 dark:bg-gray-700 rounded-3xl" />
          <div className="grid grid-cols-2 gap-4">
            <div className="h-24 bg-gray-300 dark:bg-gray-700 rounded-2xl" />
            <div className="h-24 bg-gray-300 dark:bg-gray-700 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold flex items-center">
          <div className="relative mr-3">
            <ArrowTrendingUpIcon className="h-7 w-7 text-green-600 dark:text-green-400" />
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-lg" />
          </div>
          거래 통계
        </h3>
      </div>

      <div className="flex-1 space-y-5">
        {/* Period Selector */}
        {periodButtons}
        
        {/* Main Stats Card */}
        <div className={cn(
          'rounded-3xl p-6 border shadow-sm',
          currentStats.profit >= 0
            ? 'bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 dark:from-gray-800 dark:via-gray-800 dark:to-green-900/20 border-green-200 dark:border-gray-600'
            : 'bg-gradient-to-br from-red-50 via-rose-50 to-pink-50 dark:from-gray-800 dark:via-gray-800 dark:to-red-900/20 border-red-200 dark:border-red-600'
        )}>
          <div className="flex items-center justify-between mb-4">
            <div className="p-1.5 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
              <CurrencyDollarIcon className={cn(
                'h-6 w-6',
                currentStats.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              )} />
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                {PERIOD_LABELS[selectedPeriod]} 수익
              </div>
              <div className={cn(
                'text-3xl font-bold',
                currentStats.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              )}>
                ${formatNumber(currentStats.profit)}
              </div>
              <div className={cn(
                'text-sm font-medium',
                currentStats.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              )}>
                {formatPercentage(currentStats.profitPercentage)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-600 dark:text-gray-400">거래 횟수</p>
              <p className="font-semibold">{currentStats.trades}회</p>
            </div>
            <div>
              <p className="text-gray-600 dark:text-gray-400">승률</p>
              <p className="font-semibold">{formatPercentage(currentStats.winRate, false)}</p>
            </div>
            <div>
              <p className="text-gray-600 dark:text-gray-400">평균 수익</p>
              <p className="font-semibold">${formatNumber(currentStats.avgProfit)}</p>
            </div>
          </div>
        </div>
        
        {/* Performance Metrics */}
        <div className="grid grid-cols-2 gap-4">
          {/* Total Profit Card */}
          <div className={cn(
            'rounded-2xl p-4 border shadow-sm',
            theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-slate-200'
          )}>
            <div className="flex items-center justify-between mb-3">
              <ChartBarIcon className="h-5 w-5 text-blue-500" />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                총 수익
              </span>
            </div>
            <div className="text-2xl font-bold mb-1">
              ${formatNumber(derivedStats.totalProfit)}
            </div>
            <div className={cn(
              'text-xs flex items-center',
              derivedStats.trend === 'up' ? 'text-green-600 dark:text-green-400' : 
              derivedStats.trend === 'down' ? 'text-red-600 dark:text-red-400' : 
              'text-gray-600 dark:text-gray-400'
            )}>
              <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
              {derivedStats.trend === 'up' ? '상승 추세' : 
               derivedStats.trend === 'down' ? '하락 추세' : '횡보'}
            </div>
          </div>

          {/* Average Win Rate Card */}
          <div className={cn(
            'rounded-2xl p-4 border shadow-sm',
            theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-slate-200'
          )}>
            <div className="flex items-center justify-between mb-3">
              <CalendarIcon className="h-5 w-5 text-purple-500" />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                평균 승률
              </span>
            </div>
            <div className="text-2xl font-bold mb-1">
              {formatNumber(derivedStats.avgWinRate, 1)}%
            </div>
            <div className="text-xs text-green-600 dark:text-green-400">
              {currentStats.winRate > derivedStats.avgWinRate ? '평균 이상' : '평균 이하'}
            </div>
          </div>
        </div>

        {/* Additional Stats */}
        {currentStats.trades > 0 && (
          <div className={cn(
            'rounded-2xl p-4 border',
            theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
          )}>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600 dark:text-gray-400 mb-1">최고 수익</p>
                <p className="font-semibold text-green-600 dark:text-green-400">
                  ${formatNumber(currentStats.bestTrade)}
                </p>
              </div>
              <div>
                <p className="text-gray-600 dark:text-gray-400 mb-1">최대 손실</p>
                <p className="font-semibold text-red-600 dark:text-red-400">
                  ${formatNumber(currentStats.worstTrade)}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(TradingStats);